import { useState, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';
import { usePWA } from '@/hooks/usePWA';
import {
  Home,
  FileText,
  Upload,
  BarChart3,
  Users,
  Settings,
  Menu,
  Wifi,
  WifiOff,
  Bell,
  Download,
  X
} from 'lucide-react';

const mobileNavItems = [
  { path: '/dashboard', icon: Home, label: 'Dashboard' },
  { path: '/dashboard/bills', icon: FileText, label: 'Bills' },
  { path: '/dashboard/processing', icon: Upload, label: 'Upload' },
  { path: '/dashboard/suppliers', icon: Users, label: 'Suppliers' },
  { path: '/dashboard/reports', icon: BarChart3, label: 'Reports' },
  { path: '/dashboard/user-management', icon: Settings, label: 'Settings' }
];

export const EnhancedMobileNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSwipeOpen, setIsSwipeOpen] = useState(false);
  const location = useLocation();
  const { isOnline, pendingActions } = useOfflineStorage();
  const { showInstallPrompt, installApp, dismissInstallPrompt } = usePWA();
  
  // Touch gesture handling
  const startX = useRef<number>(0);
  const startY = useRef<number>(0);
  const currentX = useRef<number>(0);
  const isDragging = useRef<boolean>(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    isDragging.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - startX.current;
      const deltaY = Math.abs(touch.clientY - startY.current);
      
      // Only start dragging if it's more horizontal than vertical
      if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
        isDragging.current = true;
        currentX.current = deltaX;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (isDragging.current) {
      const threshold = 100;
      if (currentX.current > threshold && startX.current < 50) {
        // Swipe right from left edge - open menu
        setIsSwipeOpen(true);
      } else if (currentX.current < -threshold && isSwipeOpen) {
        // Swipe left when menu is open - close menu
        setIsSwipeOpen(false);
      }
    }
    isDragging.current = false;
    currentX.current = 0;
  }, [isSwipeOpen]);

  return (
    <div className="md:hidden">
      {/* Top Mobile Header */}
      <div 
        className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Sheet open={isOpen || isSwipeOpen} onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) setIsSwipeOpen(false);
            }}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Menu className="h-5 w-5" />
                  {pendingActions > 0 && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full animate-pulse" />
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">BillPortal</h2>
                    <div className="flex items-center gap-2 mt-2">
                      {isOnline ? (
                        <div className="flex items-center gap-1 text-xs text-success">
                          <Wifi className="h-3 w-3" />
                          Online
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-xs text-warning">
                          <WifiOff className="h-3 w-3" />
                          Offline
                        </div>
                      )}
                      {pendingActions > 0 && (
                        <Badge variant="secondary" className="text-xs animate-pulse">
                          {pendingActions} pending
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* PWA Install Prompt in Sidebar */}
                  {showInstallPrompt && (
                    <div className="p-4 border-b border-border bg-accent/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Download className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Install App</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Get instant access and offline features
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => {
                            installApp();
                            setIsOpen(false);
                            setIsSwipeOpen(false);
                          }}
                          className="h-7 text-xs flex-1"
                        >
                          Install
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={dismissInstallPrompt}
                          className="h-7 w-7 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <nav className="flex-1 p-4">
                    <div className="space-y-2">
                      {mobileNavItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => {
                              setIsOpen(false);
                              setIsSwipeOpen(false);
                            }}
                            className={cn(
                              "flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105",
                              isActive
                                ? "bg-primary text-primary-foreground shadow-lg"
                                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                          >
                            <Icon className="h-4 w-4" />
                            {item.label}
                            {isActive && (
                              <div className="ml-auto w-2 h-2 bg-primary-foreground rounded-full" />
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
            
            <h1 className="text-lg font-semibold text-foreground truncate">
              {mobileNavItems.find(item => item.path === location.pathname)?.label || 'BillPortal'}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {!isOnline && (
              <WifiOff className="h-4 w-4 text-warning animate-pulse" />
            )}
            {pendingActions > 0 && (
              <Badge variant="secondary" className="text-xs animate-bounce">
                {pendingActions}
              </Badge>
            )}
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-4 w-4" />
              {pendingActions > 0 && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-warning rounded-full animate-ping" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Mobile Navigation with enhanced animations */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border">
        <div className="grid grid-cols-4 gap-1 p-2">
          {mobileNavItems.slice(0, 4).map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-300 transform hover:scale-110",
                  isActive
                    ? "text-primary bg-primary/10 shadow-lg scale-105"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                <Icon className={cn(
                  "h-5 w-5 transition-transform duration-200",
                  isActive && "animate-pulse"
                )} />
                <span className="text-xs font-medium">{item.label}</span>
                {isActive && (
                  <div className="w-1 h-1 bg-primary rounded-full animate-pulse" />
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Swipe indicator */}
      <div className="fixed left-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-primary/20 rounded-r-full opacity-50 transition-opacity duration-300 hover:opacity-100" />
    </div>
  );
};